import isTestEnvironment from '../isTestEnvironment';
import { getCachedMessagesByKeys, addMessageToCache, getCachedMessageByKey } from './messageCache';

const pendingKeys = new Set();
const DELAY = 30;

function showKeysOnly() {
    return localStorage.getItem('i18n.showMessageKeys') === 'true' || isTestEnvironment();
}

async function fetchMessagesByKeys(messageKeys, lang) {
    const keyQueryParams = messageKeys
        .map((messageKey) => `keys=${encodeURIComponent(messageKey)}`)
        .join('&');
    const response = await fetch(`/ui/i18n/messages-by-keys/${lang}?${keyQueryParams}`);
    const { responseStatus, data } = await response.json();
    if (responseStatus === 'SUCCESS') {
        return data.messages;
    }
    throw new Error(responseStatus);
}

function getUncachedMessageKeys(messageKeys) {
    const cachedMessages = getCachedMessagesByKeys(messageKeys);
    return cachedMessages
        .map((message, i) => [messageKeys[i], message])
        .filter(([_, message]) => message === null)
        .map(([messageKey]) => messageKey);
}

function loadBatchedMessages(lang) {
    return new Promise((resolve) => {
        setTimeout(async () => {
            const uncachedMessageKeys = getUncachedMessageKeys([...pendingKeys]);

            if (uncachedMessageKeys.length) {
                pendingKeys.clear();
                const messages = await fetchMessagesByKeys(uncachedMessageKeys, lang);
                uncachedMessageKeys.forEach((messageKey, i) =>
                    // Store messages not found as undefined. Null is used for "unknown state". We know this is
                    //  undefined if it's not returned from the server.
                    addMessageToCache(messageKey, messages[i] ? messages[i] : undefined),
                );
            }
            resolve();
        }, DELAY);
    });
}

function waitForCachedMessages(messageKeys) {
    const messages = getCachedMessagesByKeys(messageKeys);
    if (!messages.includes(null)) {
        return Promise.resolve(messages);
    }

    return new Promise((resolve) => {
        setTimeout(() => {
            resolve(waitForCachedMessages(messageKeys));
        }, DELAY);
    });
}

/**
 *
 * @param {String[]} messageKeys Array of fully qualified message keys to fetch.
 *      ex: [group.name]
 *
 * @param lang {String} User language key for messages that aren't cached yet
 * @returns {String[]} Array of translated messages corresponding to messageKeys, in the
 *     defined order. Returns "undefined" if a message cannot be found.
 */
export default async function getMessagesByKeys(messageKeys, lang) {
    if (showKeysOnly()) {
        return messageKeys;
    }

    let shouldWaitForBatch = false;
    messageKeys.forEach((messageKey) => {
        if (!getCachedMessageByKey(messageKey)) {
            shouldWaitForBatch = true;
            pendingKeys.add(messageKey);
        }
    });
    if (shouldWaitForBatch) {
        await loadBatchedMessages(lang);
    }
    return waitForCachedMessages(messageKeys);
}
